export const ASSISTANT_SESSION_KEY = "harris_ai_assistant_session";
export const ASSISTANT_TAB_INSTANCE_KEY = "harris_ai_assistant_tab_instance";
const CONVERSATIONS_STORAGE_KEY = "ai_assistant_conversations";

const readAllConversationsFromLocalStorage = () => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(CONVERSATIONS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed;
    }
  } catch (error) {
    console.warn("[AI Assistant][LocalStorage] Failed to read conversations", error);
  }
  return {};
};

const writeAllConversationsToLocalStorage = (conversations) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CONVERSATIONS_STORAGE_KEY,
      JSON.stringify(conversations || {}),
    );
  } catch (error) {
    console.warn("[AI Assistant][LocalStorage] Failed to persist conversations", error);
  }
};

const readConversationFromLocalStorage = (conversationId) => {
  if (!conversationId) return null;
  const conversations = readAllConversationsFromLocalStorage();
  return conversations?.[conversationId] || null;
};

const writeConversationToLocalStorage = (conversation) => {
  if (!conversation?.conversationId) return null;
  const conversations = readAllConversationsFromLocalStorage();
  const nextConversations = {
    ...conversations,
    [conversation.conversationId]: conversation,
  };
  writeAllConversationsToLocalStorage(nextConversations);
  return conversation.conversationId;
};

const clearLocalStorageConversations = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(CONVERSATIONS_STORAGE_KEY);
  } catch (error) {
    console.warn("[AI Assistant][LocalStorage] Failed to clear conversations", error);
  }
};

export const getConversation = async (conversationId) =>
  Promise.resolve(readConversationFromLocalStorage(conversationId)).then((result) => {
    console.debug("[AI Assistant][LocalStorage] getConversation", {
      conversationId,
      hasResult: Boolean(result),
      messageCount: Array.isArray(result?.messages) ? result.messages.length : 0,
    });
    return result;
  });

export const saveConversation = async (conversation) =>
  Promise.resolve(writeConversationToLocalStorage(conversation)).then((result) => {
    console.debug("[AI Assistant][LocalStorage] saveConversation", {
      conversationId: conversation?.conversationId,
      messageCount: Array.isArray(conversation?.messages)
        ? conversation.messages.length
        : 0,
    });
    return result;
  });

export const upsertConversationSnapshot = async ({
  conversationId,
  clientId,
  messages = [],
  historyMetadata = null,
  sourceConversationId = null,
}) => {
  const existing = await getConversation(conversationId);
  const now = new Date().toISOString();
  const nextConversation = {
    conversationId,
    clientId: clientId ?? existing?.clientId ?? null,
    messages,
    historyMetadata,
    sourceConversationId: sourceConversationId ?? existing?.sourceConversationId ?? null,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
  await saveConversation(nextConversation);
  return nextConversation;
};

export const clearAssistantStore = async () => {
  clearLocalStorageConversations();
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.removeItem(ASSISTANT_SESSION_KEY);
      window.sessionStorage.removeItem(ASSISTANT_TAB_INSTANCE_KEY);
    } catch (error) {
      console.warn("[AI Assistant] Failed to clear tab id", error);
    }
  }
};
